<template>
    <div>
        <hts-router-drawer
            :title="$t('labels.bankAccount')"
            size="large">
            <div class="mb-3 text-right">
                <hts-form-button
                    v-if="checkPermission(layoutPermissions['partner_bank_add'])"
                    color="main"
                    icon-name="hi-plus-circle-icon"
                    icon-position="right"
                    @click="openAddBusinessOpportunity">
                    {{ $t('labels.addBankAccount') }}
                </hts-form-button>
            </div>
            <hts-data-table
                ref="dataTables"
                :headers="cooperatorBusinessOpportunity"
                :server="server"
                store-name="cooperatorDataTable"
                store-key="CooperatorBank"
                :use-archive="useArchive"
                :use-restore="useRestore"
                use-selection />
            <add-bank
                v-if="checkPermission(layoutPermissions['partner_bank_add'])"
                ref="addBusinessOpportunity"
                :code-cooperator="cooperatorCode"
                @refresh-tables="refreshTables" />
            <hts-modal
                v-if="checkPermission(layoutPermissions['partner_bank_delete'])"
                :show="showPopupDeleteBank"
                :title="$t('labels.deleteBankAccount')"
                type="confirm"
                :content="$t('labels.confirmDeleteBankAccount')"
                @hide="closePopupDeleteBank"
                @confirm="handleDeleteBank" />
        </hts-router-drawer>
        <router-view @refresh-tables="refreshTables" />
    </div>
</template>

<script setup lang="ts">
import AddBank from './bank/-components/add.vue'
import { checkPermission } from '@/utils/permission-check'
import layoutPermissions from '~/data/permissions/layout-permissions'

import actions from '~/pages/trucker-[id]/partner/-components/actions.vue'

const cooperatorCode = computed(() => useRoute().params.idpartner)
const { $formatters } = useNuxtApp()
const showPopupDeleteBank = ref(false)
const visible = ref(false)

const bankAccountId = ref('')
const dataTables = ref()
const addBusinessOpportunity: any = ref(null)

const server = {
  method: 'post',
  url: '/partner/bank/' + cooperatorCode.value + '/list'
}

const props = defineProps({})

const openAddBusinessOpportunity = (customer: any) => {
  if (addBusinessOpportunity.value) {
    addBusinessOpportunity.value.open(cooperatorCode.value)
  }
}

const openEditBank = (bank: any) => {
  useRouter().push({ name: 'c-id-partner-idpartner-bank-idbank-edit', params: { idbank: bank._id } })
}

const openDeleteBank = (data: any) => {
  bankAccountId.value = data?._id
  showPopupDeleteBank.value = true
}

const cooperatorBusinessOpportunity = [
  {
    field: 'bank',
    headerName: $t('labels.bank'),
    sortable: true,
    minWidth: 450,
    filter: 'agTextColumnFilter',
    valueFormatter: (params: any) => {
      return $t(`labels.bankModule.${params?.data?.bank}`) + ` (${params?.data?.bank})`
    }
  },
  {
    field: 'bankAccountNumber',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.bankAccountNumber')
  },
  {
    field: 'bankBranch',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.bankBranch')
  },
  {
    field: 'beneficiary',
    headerName: $t('labels.beneficiary'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'currency',
    headerName: $t('labels.currency'),
    sortable: true,
    filter: 'agTextColumnFilter',
    valueFormatter: (params: any) => {
      return params?.data?.currency ? $t(`labels.${params.data.currency}`) : ''
    }
  },
  {
    field: 'createdAt',
    filter: 'agDateColumnFilter',
    headerName: $t('labels.createdAt'),
    sortable: true,
    valueGetter: (params: any) => {
      return params.data.createdAt ? $formatters.datetime(params.data.createdAt) : ''
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return params.data.updatedAt ? $formatters.datetime(params.data.updatedAt) : ''
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: actions,
    cellRendererParams: {
      storeKey: 'cooperatorDataTable',
      storeName: 'CooperatorBank',
      items: [
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.partner_bank_edit),
          click: openEditBank
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.partner_bank_delete),
          click: openDeleteBank,
          isArchive: true
        }
      ]
    }
  }
]

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const closePopupDeleteBank = () => {
  showPopupDeleteBank.value = false
  bankAccountId.value = ''
}

const handleDeleteBank = async () => {
  const api = $urlPartner.partner.bank.delete
  const data = await $api(api, { uriParams: { cooperatorId: cooperatorCode.value }, body: { ids: [bankAccountId.value] } })
  const resData: any = data?.data?.value
  if (resData?.success) {
    process.client && $toast().success($t('labels.deleteBankAccountSuccess'))
    refreshTables()
    closePopupDeleteBank()
  }
}
const useArchive = { api: $urlPartner.partner.bank.archive, permissions: checkPermission(layoutPermissions.partner_bank_archive) }
const useRestore = { api: $urlPartner.partner.bank.restore, permissions: checkPermission(layoutPermissions.partner_bank_restore) }
</script>
/partner
